import { useState } from 'react'
import firebase from '../../firebase/compat'
import { NAME_STEP_TEXTS } from './linkStepTexts'
import { HONEYPOT_KEY, MAX_NAME_PART, hasControlChars, isValidNamePart } from '../../helpers/questionnaire/nameStepRules'

// De naamstap vóór een openbare vragenlijst (Research.AskNameFirst).
//
// Nodig naast AddressStep omdat een naam geen adres is: er valt niets op te
// zoeken of te kiezen, en de server koppelt nooit zelf (een naam is niet
// uniek). matchQuestionnaireName legt de naam alleen vast; een medewerker
// koppelt de invulling op de kaart 'Deelnemers om te koppelen'. De server
// antwoordt dus altijd hetzelfde, en deze stap kent maar twee uitkomsten:
// opgeslagen of mislukt.
//
// Valt dicht: de vragen verschijnen pas na een geslaagde aanroep. Een fout
// zegt 'probeer opnieuw' en laat de invuller niet stil zonder naam door.
// Deelt de opmaak van de adresstap (address-step-*), zodat de twee stappen er
// hetzelfde uitzien.
// AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
// AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading

/**
 * @typedef {'invalid'|'chars'|'error'|''} NameStepProblem
 */

/**
 * @param {object} props
 * @param {string} props.client - CompagnyID uit de url
 * @param {string} props.research - ResearchID uit de url
 * @param {string} props.moment - MomentID uit de url
 * @param {string} props.sessionId - De invulsessie
 * @param {Record<string, string>} props.translations - Vertalingen van de vragenlijst; Nederlands als terugval
 * @param {() => void} props.onDone - Aangeroepen zodra de naam is opgeslagen
 */
const NameStep = ({ client, research, moment, sessionId, translations, onDone }) => {
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const [saving, setSaving] = useState(false)
    const [problem, setProblem] = useState(/** @type {NameStepProblem} */ (''))

    /** @param {string} key */
    const t = (key) => translations?.[key] || NAME_STEP_TEXTS[key]

    const send = async () => {
        if (saving) return
        if (!firstName.trim() || !lastName.trim()) {
            setProblem('invalid')
            return
        }
        // Dezelfde regels als de server: een naam die hij weigert, gaat niet weg.
        if (!isValidNamePart(firstName) || !isValidNamePart(lastName)) {
            setProblem(hasControlChars(firstName) || hasControlChars(lastName) ? 'chars' : 'invalid')
            return
        }

        setSaving(true)
        setProblem('')
        try {
            const call = firebase.app().functions('europe-west1').httpsCallable('matchQuestionnaireName')
            const { data } = await call({
                client,
                researchId: research,
                momentId: moment,
                sessionId,
                firstName,
                lastName,
                [HONEYPOT_KEY]: honeypot,
            })

            if (data?.ok) {
                // onDone haalt deze stap van het scherm: daarna geen state meer
                // zetten, dus `saving` niet terug in een finally.
                onDone()
                return
            }
            setProblem('error')
            setSaving(false)
        } catch (error) {
            // Zichtbaar laten, niet doorlaten: zonder naam kan niemand de
            // antwoorden koppelen. AI-CONTEXT: docs/pitfalls.md#error-catch-all
            console.error('Naamstap: opslaan mislukt', error)
            setProblem('error')
            setSaving(false)
        }
    }

    const submitHandler = (e) => {
        e.preventDefault()
        send()
    }

    const messages = {
        invalid: t('Naamstap ongeldig'),
        chars: t('Naamstap tekens'),
        error: t('Naamstap fout'),
    }

    return (
        <form className="address-step" onSubmit={submitHandler} noValidate>
            <h2>{t('Naamstap titel')}</h2>
            <p>
                {t('Naamstap uitleg')}
            </p>

            <label className="address-step-field">
                <span>{t('Voornaam')}</span>
                <input type="text" autoComplete="given-name" maxLength={MAX_NAME_PART} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </label>
            <label className="address-step-field">
                <span>{t('Achternaam')}</span>
                <input type="text" autoComplete="family-name" maxLength={MAX_NAME_PART} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </label>

            {/* Lokveld: buiten beeld, niet in de tabvolgorde, niet voor schermlezers. */}
            <input
                className="intake-honeypot"
                type="text"
                name={HONEYPOT_KEY}
                data-testid="name-step-honeypot"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
            />

            {problem && <p className="address-step-problem" role="alert">{messages[problem]}</p>}

            <div className="questionnaire-button-container">
                <button type="submit" disabled={saving}>
                    {saving ? t('Bezig') : t('Verder')}
                </button>
            </div>
        </form>
    )
}

export default NameStep
